import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, Injector } from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { ConfigService } from './services/config/config.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AuthService } from './services/auth/auth.service';
import { MediaLibraryVideoService } from './services/media-library/handler-services/media-library-video.service';
import { MediaLibraryPdfService } from './services/media-library/handler-services/media-library-pdf.service';
import { MediaLibraryImageService } from './services/media-library/handler-services/media-library-image.service';
import { IMediaLibraryHandlerStrategy } from './models/util/mediaLibraryHandlerStrategy';
import * as Sentry from "@sentry/angular";
import { ServiceLocator } from './models/serviceLocator';
import { GlobalErrorHandler } from './services/global-error/global-error.service';

export function HttpLoaderFactory(http: HttpClient, configService: ConfigService): TranslateHttpLoader {
	return new TranslateHttpLoader(http, environment.translations.prefix, environment.translations.suffix + `&v=${configService.getConfig()?.translationVersion}`);
}

function loadTenantConfig(configService: ConfigService, authService: AuthService, injector: Injector): () => Promise<void> {
	return async () => {
		ServiceLocator.injector = injector;
		configService.setProjectTheme(environment.stylesUrl);
		if (!environment.production && configService.shouldUserLogOut()) {
			await authService.logout();
		}
		return configService.loadConfig();
	};
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(routes),
		provideAnimationsAsync(),
		importProvidersFrom(
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useFactory: HttpLoaderFactory,
					deps: [HttpClient, ConfigService]
				}
			}),
			NgxSkeletonLoaderModule.forRoot({ animation: 'pulse' }),
		),
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [ConfigService, AuthService, Injector],
			useFactory: loadTenantConfig
		},
		{
			deps: [
				MediaLibraryVideoService,
				MediaLibraryPdfService,
				MediaLibraryImageService
			],
			provide: 'MediaLibraryHandlers',
			useFactory: (
				mediaLibraryVideoService: IMediaLibraryHandlerStrategy,
				mediaLibraryPdfService: IMediaLibraryHandlerStrategy,
				mediaLibraryImageService: IMediaLibraryHandlerStrategy
			) => [
				mediaLibraryVideoService,
				mediaLibraryPdfService,
				mediaLibraryImageService
			]
		},
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler()
		},
		{
			provide: Sentry.TraceService,
			deps: [Router]
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler
		}
	]
};
